import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
    Plus, Search, Filter, ChevronLeft, ChevronRight,
    Pencil, Trash2, ToggleLeft, ToggleRight, Layers3, Image as ImageIcon
} from "lucide-react";

import { Category } from "@/services/types/domain";
import { categoriesApi } from "@/services/api/categories"; // usa tus métodos ya existentes
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import CategoryFormModal from "@/components/admin/CategoryFormModal";
import ConfirmarDialog from "@/components/ui/ConfirmarDialog";
import { UPLOADS_URL } from "@/utils/utils";

const DEBOUNCE_MS = 500;

const isActive = (val: boolean | number | undefined) => val === true || val === 1;

const AdminCategoriesPage: React.FC = () => {
    const [rows, setRows] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);

    const [meta, setMeta] = useState({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
    });

    // filtros
    const [q, setQ] = useState("");
    const [isActiveFilter, setIsActiveFilter] = useState<"" | "1" | "0">("");
    const [parentFilter, setParentFilter] = useState<"" | number>("");
    const [parents, setParents] = useState<Category[]>([]);

    // UI state
    const [showForm, setShowForm] = useState<{
        open: boolean;
        mode: "create" | "edit";
        category?: Category | null;
    }>({ open: false, mode: "create", category: null });

    const [confirm, setConfirm] = useState<{ open: boolean; id: number | null; name?: string }>({
        open: false,
        id: null,
    });

    const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
    const searchRef = useRef<NodeJS.Timeout>();

    // Helpers de listado (usamos tus servicios; si tu service ya adapta la respuesta, perfecto)
    const fetchData = async (page = 1) => {
        try {
            setLoading(true);

            // Esperado: categoriesApi.list({ page, per_page, q, is_active, parent_id })
            // Debe devolver algo tipo: { data: Category[], meta: { current_page, per_page, total, last_page } }
            const res = await (categoriesApi as any).getCategories({
                page,
                per_page: meta.per_page,
                q: q || undefined,
                is_active: isActiveFilter === "" ? undefined : Number(isActiveFilter),
                parent_id: parentFilter === "" ? undefined : Number(parentFilter),
            });
            console.log("res", res);
            const data: Category[] = res?.data ?? res?.categories ?? [];
            const m = res?.meta ?? {
                current_page: res?.page ?? page,
                per_page: res?.per_page ?? meta.per_page,
                total: res?.total ?? data.length,
                last_page: res?.total_pages ?? 1,
            };

            setRows(data);
            setMeta({
                current_page: Number(m.current_page),
                per_page: Number(m.per_page),
                total: Number(m.total),
                last_page: Number(m.last_page),
            });
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudieron cargar las categorías");
        } finally {
            setLoading(false);
        }
    };

    const loadParents = async () => {
        try {
            // Si ya tienes un método para combos, úsalo. Ej: categoriesApi.getAllForSelect()
            // Fallback: pedir muchas por página.
            const allRes = (await (categoriesApi as any).getCategories?.({})) ??
                (await (categoriesApi as any).list({ page: 1, per_page: 1000 }));
            const all: Category[] = Array.isArray(allRes) ? allRes : (allRes?.data ?? []);
            setParents(all);
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        loadParents();
    }, []);

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActiveFilter, parentFilter]);

    useEffect(() => {
        if (searchRef.current) clearTimeout(searchRef.current);
        searchRef.current = setTimeout(() => fetchData(1), DEBOUNCE_MS);
        return () => { if (searchRef.current) clearTimeout(searchRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const goPage = (p: number) => {
        if (p < 1 || p > meta.last_page) return;
        fetchData(p);
    };

    const parentName = (c: Category) => {
        if (c.parent_name) return c.parent_name;
        if (!c.parent_id) return "—";
        return parents.find((p) => p.id === c.parent_id)?.name || "—";
    };

    const toggleActive = async (cat: Category) => {
        if (!cat.id) return;
        const id = Number(cat.id);
        const next = !isActive(cat.is_active);

        setBusyIds((s) => new Set(s).add(id));
        try {
            // Si ya tienes PATCH en tu service: categoriesApi.toggleActive(id, next)
            const updated = await (categoriesApi as any).toggleActive?.(id, next);
            if (updated) {
                setRows((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: updated.is_active } as Category : c)));
            } else {
                // Fallback: update local si tu service no devuelve el registro
                setRows((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: next ? 1 : 0 } as Category : c)));
            }
            toast.success(`Categoría ${next ? "activada" : "desactivada"}`);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.message || "No se pudo cambiar el estado");
        } finally {
            setBusyIds((s) => {
                const nextSet = new Set(s);
                nextSet.delete(id);
                return nextSet;
            });
        }
    };

    const onDelete = async () => {
        if (!confirm.id) return;
        const id = confirm.id;
        setBusyIds((s) => new Set(s).add(id));
        try {
            // categoriesApi.remove(id)
            await (categoriesApi as any).deleteCategory(id);
            setRows((prev) => prev.filter((c) => c.id !== id));
            toast.success("Categoría eliminada");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.message || "No se pudo eliminar");
        } finally {
            setBusyIds((s) => {
                const nextSet = new Set(s);
                nextSet.delete(id);
                return nextSet;
            });
            setConfirm({ open: false, id: null });
        }
    };

    //const empty = !loading && rows.length === 0;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-7xl space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Layers3 className="w-6 h-6" />
                            Gestión de categorías
                        </h1>
                        <p className="text-gray-600 text-sm">Crea y organiza las categorías de tu marketplace</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => setShowForm({ open: true, mode: "create", category: null })}
                            leftIcon={<Plus className="w-4 h-4" />}
                        >
                            Nueva categoría
                        </Button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 rounded-2xl border bg-white p-3 sm:p-4">
                    <div className="lg:col-span-5">
                        <Input
                            label="Buscar"
                            placeholder="Nombre o slug…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            leftIcon={<Search className="w-4 h-4" />}
                            fullWidth
                        />
                    </div>

                    <div className="lg:col-span-3">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                        <div className="relative">
                            <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <select
                                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm"
                                value={isActiveFilter}
                                onChange={(e) => setIsActiveFilter(e.target.value as any)}
                            >
                                <option value="">Todos</option>
                                <option value="1">Activos</option>
                                <option value="0">Inactivos</option>
                            </select>
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Padre</label>
                        <select
                            className="w-full border rounded-md px-3 py-2 text-sm"
                            value={parentFilter as any}
                            onChange={(e) => {
                                const val = e.target.value;
                                setParentFilter(val === "" ? "" : Number(val));
                            }}
                        >
                            <option value="">Todos</option>
                            {parents
                                .filter((p) => p.parent_id == null) // raíces
                                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                                .map((p) => (
                                    <option key={p.id ?? `p-${p.name}`} value={p.id ?? ""}>
                                        {p.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>

                {/* Tabla */}
                <div className="rounded-2xl border bg-white">
                    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs font-semibold text-gray-500">
                        <div className="col-span-4">Categoría</div>
                        <div className="col-span-2">Padre</div>
                        <div className="col-span-2">Slug</div>
                        <div className="col-span-1 text-right">Orden</div>
                        <div className="col-span-1 text-center">Estado</div>
                        <div className="col-span-2 text-right">Acciones</div>
                    </div>

                    <div className="divide-y">
                        {loading &&
                            Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="px-4 py-4 animate-pulse">
                                    <div className="h-6 bg-gray-100 rounded w-2/3" />
                                </div>
                            ))}

                        {!loading && rows.length === 0 && (
                            <div className="px-4 py-8 text-center text-gray-600">No hay categorías para mostrar.</div>
                        )}

                        {!loading &&
                            rows.map((c) => {
                                const active = isActive(c.is_active);
                                const img = c.image_url || c.image || null;

                                return (
                                    <div
                                        key={c.id ?? `cat-${c.slug}-${c.name}`}
                                        className="px-4 py-3 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-center"
                                    >
                                        {/* Nombre + mini */}
                                        <div className="md:col-span-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-10 rounded-md overflow-hidden border bg-gray-50">
                                                    {img ? (
                                                        <img src={UPLOADS_URL + img} alt={c.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full grid place-items-center text-gray-300">
                                                            <ImageIcon className="w-5 h-5" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-gray-900">{c.name}</div>
                                                    {!!c.description && (
                                                        <div className="text-xs text-gray-500 line-clamp-1">{c.description}</div>
                                                    )}
                                                    <div className="mt-1 flex items-center gap-1">
                                                        {active ? (
                                                            <Badge variant="success">Activo</Badge>
                                                        ) : (
                                                            <Badge variant="outline">Inactivo</Badge>
                                                        )}
                                                        <Badge variant="secondary">Orden: {c.sort_order}</Badge>
                                                        {typeof c.product_count === "number" && (
                                                            <Badge variant="secondary">{c.product_count} productos</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Padre */}
                                        <div className="md:col-span-2 text-sm text-gray-700">{parentName(c)}</div>

                                        {/* Slug */}
                                        <div className="md:col-span-2 text-sm text-gray-700">{c.slug}</div>

                                        {/* Orden */}
                                        <div className="md:col-span-1 text-right text-sm text-gray-700">
                                            {c.sort_order}
                                        </div>

                                        {/* Estado */}
                                        <div className="md:col-span-1 flex md:justify-center">
                                            {c.id && (
                                                <button
                                                    className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
                                                    onClick={() => toggleActive(c)}
                                                    disabled={busyIds.has(Number(c.id))}
                                                    title={active ? "Desactivar" : "Activar"}
                                                >
                                                    {active ? (
                                                        <ToggleRight className="w-5 h-5 text-green-600" />
                                                    ) : (
                                                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                                                    )}
                                                    <span className="hidden sm:inline">{active ? "Activo" : "Inactivo"}</span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Acciones */}
                                        <div className="md:col-span-2 flex md:justify-end gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                leftIcon={<Pencil className="w-4 h-4" />}
                                                onClick={() => setShowForm({ open: true, mode: "edit", category: c })}
                                            >
                                                Editar
                                            </Button>
                                            {c.id && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    leftIcon={<Trash2 className="w-4 h-4" />}
                                                    onClick={() => setConfirm({ open: true, id: Number(c.id), name: c.name })}
                                                >
                                                    Eliminar
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Paginación */}
                <div className="mt-6 flex items-center justify-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goPage(meta.current_page - 1)}
                        disabled={meta.current_page <= 1}
                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                        Anterior
                    </Button>
                    <span className="text-sm text-gray-700">
                        Página <strong>{meta.current_page}</strong> de <strong>{meta.last_page}</strong>
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goPage(meta.current_page + 1)}
                        disabled={meta.current_page >= meta.last_page}
                        rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                        Siguiente
                    </Button>
                </div>
            </div>

            {/* Modal crear/editar */}
            {showForm.open && (
                <CategoryFormModal
                    open={showForm.open}
                    mode={showForm.mode}
                    category={showForm.category || undefined}
                    parents={parents}
                    onClose={() => setShowForm({ open: false, mode: "create", category: null })}
                    onSaved={() => {
                        setShowForm({ open: false, mode: "create", category: null });
                        fetchData(meta.current_page);
                        loadParents();
                    }}
                />
            )}

            {/* Confirm eliminar */}
            {confirm.open && (
                <ConfirmarDialog
                    open={confirm.open}
                    title="Eliminar categoría"
                    description={
                        <>
                            ¿Seguro que deseas eliminar <strong>{confirm.name}</strong>?<br />
                            Esta acción no se puede deshacer.
                        </>
                    }
                    confirmText="Eliminar"
                    variant="destructive"
                    onCancel={() => setConfirm({ open: false, id: null })}
                    onConfirm={onDelete}
                    busy={confirm.id ? busyIds.has(confirm.id) : false}
                />
            )}
        </div>
    );
};

export default AdminCategoriesPage;
